import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { availableLanguages, currentLang, setLang, t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { snapshot, useOffline } from '../lib/offline.ts';
import { comingFor, useWaitingVisitors } from '../lib/frontdesk.ts';
import { useClockDue } from '../lib/team.ts';
import { inApp } from '../lib/native.ts';
import { checkSoon, usePrintProblems } from '../lib/printing.ts';
import { usePlaces } from '../lib/places.tsx';
import { device, useSession } from '../lib/session.tsx';
import { go, otherAccounts, restore, stashCurrent, unsentSales } from '../lib/accounts.ts';
import { useUi } from '../lib/ui-state.tsx';
import { CalendarReminders } from './CalendarReminders.tsx';
import { AskAi, type AskAiPrompt } from './AskAi.tsx';
import { CommandPalette } from './Command.tsx';
import { quickCreates } from './search.ts';
import { Icon } from './Icon.tsx';
import { BrandMark } from './BrandMark.tsx';
import { entryFor, GROUPS, useNavEntries, usePref, type NavEntry, type NavGroup } from './nav.tsx';
import { Button, Dialog, IconButton } from './ui.tsx';

/**
 * The application frame: a deep-navy sidebar with the places this person may open (grouped,
 * collapsible, with favourites), a quiet top bar (where you are, search, notifications, help,
 * profile) and, on a phone, a bottom bar with a menu that slides in.
 */
export function Shell({ children, live }: { children: ReactNode; live?: boolean | null }) {
  const { me, tenant, can } = useSession();
  const { fail, toast } = useUi();
  const { entries, calendarSoon } = useNavEntries();
  const phoneApp = inApp();
  const location = useLocation();
  const offline = useOffline();
  const sells = can('pos.pay');
  // Keep this device's copy of the menu and shift fresh, for when the connection drops.
  useEffect(() => {
    if (!sells || !offline.online) return;
    const age = Date.now() - Date.parse(snapshot.get()?.saved_at ?? '1970-01-01');
    if (age > 10 * 60_000) void snapshot.refresh(device.register);
  }, [sells, offline.online, location.pathname]);
  const onTill = location.pathname === '/offline';
  const clockDue = useClockDue(can('team.self') && offline.online);
  const visitors = useWaitingVisitors(can('frontdesk.plan') && offline.online);
  const printProblems = usePrintProblems(can('printing.use') && offline.online);
  const retryPrints = async () => {
    try {
      for (const p of printProblems) await action('printing.retry', { printer: p.id });
      toast(t('printers.retried'));
      checkSoon();
    } catch (err) {
      fail(err);
    }
  };
  const [collapsed, setCollapsed] = usePref<boolean>('nav.collapsed', false);
  const [drawer, setDrawer] = useState(false);
  const [search, setSearch] = useState(false);
  // ERP PRO AI (Ctrl J / Cmd J): for people whose role and plan include it.
  const askAi = can('ai.use') && offline.online;
  const [ai, setAi] = useState(false);
  const [aiPrompt, setAiPrompt] = useState<AskAiPrompt | null>(null);
  // "Ask AI about this" on any screen: the panel opens and asks it.
  useEffect(() => {
    const onAsk = (e: Event) => {
      if (!askAiRef.current) return;
      const text = (e as CustomEvent<string>).detail;
      setAiPrompt((p) => ({ text, n: (p?.n ?? 0) + 1 }));
      setAi(true);
    };
    window.addEventListener('erp:ask-ai', onAsk);
    return () => window.removeEventListener('erp:ask-ai', onAsk);
  }, []);
  useEffect(() => setDrawer(false), [location.pathname]);
  // Ctrl K / Cmd K opens the search from anywhere, as in every modern business app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearch((o) => !o);
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'j' && askAiRef.current) {
        e.preventDefault();
        setAi((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const here = entryFor(entries, location.pathname);
  const askAiRef = useRef(askAi);
  askAiRef.current = askAi;
  const group = here ? GROUPS.find((g) => g.key === here.group) : undefined;
  const hasNav = entries.length > 1;
  const [favs] = usePref<string[]>('nav.fav', []);
  const bottom = useMemo(() => {
    const fav = favs.map((to) => entries.find((e) => e.to === to)).filter((e): e is NavEntry => !!e);
    return [...fav, ...entries.filter((e) => !favs.includes(e.to))].slice(0, entries.length > 5 ? 4 : 5);
  }, [entries, favs]);
  const waiting = entries.reduce((n, e) => n + (e.badge ?? 0), 0);

  return (
    <div className={`shell${collapsed ? ' side-collapsed' : ''}`}>
      {hasNav ? (
        <>
          <Sidebar entries={entries} collapsed={collapsed} onCollapse={() => setCollapsed(!collapsed)} open={drawer} onClose={() => setDrawer(false)} tenantName={tenant?.name ?? ''} />
          {/* On a phone: the most used places under the thumb, everything else in the menu. */}
          <nav className="bottombar" aria-label={t('nav.main')}>
            {bottom.map((x) => (
              <NavLink key={x.to} to={x.to} className={({ isActive }) => `bottombar-link${isActive ? ' active' : ''}`}>
                <Icon name={x.icon} size={22} />
                <span className="bottombar-label">{x.label}</span>
                {x.badge ? (
                  <span className="nav-badge" aria-label={x.badgeLabel}>
                    {x.badge > 99 ? '99+' : x.badge}
                  </span>
                ) : null}
              </NavLink>
            ))}
            {entries.length > 5 ? (
              <button type="button" className="bottombar-link" aria-expanded={drawer} onClick={() => setDrawer(true)}>
                <Icon name="menu" size={22} />
                <span className="bottombar-label">{t('nav.more')}</span>
                {waiting ? (
                  <span className="nav-badge" aria-label={t('nav.more_waiting', { n: waiting })}>
                    {waiting > 99 ? '99+' : waiting}
                  </span>
                ) : null}
              </button>
            ) : null}
          </nav>
        </>
      ) : null}
      <CalendarReminders soon={calendarSoon} />
      {search ? <CommandPalette entries={entries} onClose={() => setSearch(false)} /> : null}
      {ai && askAi ? <AskAi page={here?.label} path={location.pathname} prompt={aiPrompt} onClose={() => setAi(false)} /> : null}
      <div className="shell-main">
        <header className="topbar">
          <div className="topbar-where">
            <span className="app-mark topbar-mark cube" aria-hidden="true">
              <BrandMark size={26} />
            </span>
            <div className="topbar-titles">
              {group ? <span className="topbar-group">{t(group.label)}</span> : <span className="topbar-group">{tenant?.name}</span>}
              <span className="topbar-page">{here?.label ?? t('app.name')}</span>
            </div>
          </div>
          {hasNav ? (
            <button type="button" className="topbar-search" onClick={() => setSearch(true)} aria-label={t('shell.search')}>
              <Icon name="search" size={18} />
              <span className="topbar-search-text">{t('shell.search_hint')}</span>
              <kbd className="topbar-kbd">{t('shell.search_keys')}</kbd>
            </button>
          ) : null}
          <div className="topbar-tools">
            {live === false ? (
              <span className="badge badge-warn" title={t('live.reconnecting_hint')}>
                <Icon name="wifiOff" size={14} />
                <span>{t('live.reconnecting')}</span>
              </span>
            ) : null}
            {hasNav ? <IconButton className="topbar-search-icon" icon="search" label={t('shell.search')} onClick={() => setSearch(true)} /> : null}
            <PlaceSwitch />
            {askAi ? (
              <button type="button" className={`topbar-ai${ai ? ' active' : ''}`} aria-expanded={ai} onClick={() => setAi((o) => !o)} title={t('ai.open_hint')}>
                <Icon name="sparkle" size={18} />
                <span className="topbar-ai-text">{t('ai.open')}</span>
              </button>
            ) : null}
            {hasNav ? <CreateMenu /> : null}
            {hasNav ? (
              <NavLink to="/apps" className={({ isActive }) => `icon-btn btn-ghost topbar-apps${isActive ? ' active' : ''}`} title={t('shell.apps')} aria-label={t('shell.apps')}>
                <Icon name="apps" />
              </NavLink>
            ) : null}
            {phoneApp ? (
              <NavLink to="/phone" className={({ isActive }) => `icon-btn btn-ghost${isActive ? ' active' : ''}`} title={t('phone.title')} aria-label={t('phone.title')}>
                <Icon name="phone" />
              </NavLink>
            ) : null}
            <Notifications entries={entries} />
            <HelpMenu />
            <ProfileMenu name={me?.name ?? ''} business={tenant?.name ?? ''} />
          </div>
        </header>
        {!offline.online && !onTill ? (
          <div className="conn-bar conn-bar-warn" role="status">
            <Icon name="wifiOff" size={18} />
            <span>{sells ? t('offline.bar') : t('offline.bar_view')}</span>
            {sells ? (
              <NavLink to="/offline" className="btn btn-secondary btn-sm">
                <span className="btn-label">{t('offline.open_till')}</span>
              </NavLink>
            ) : null}
          </div>
        ) : offline.online && offline.failed && !onTill ? (
          <div className="conn-bar conn-bar-danger" role="status">
            <Icon name="alert" size={18} />
            <span>{tn('offline.failed_bar', offline.failed)}</span>
            <NavLink to="/offline" className="btn btn-secondary btn-sm">
              <span className="btn-label">{t('offline.review')}</span>
            </NavLink>
          </div>
        ) : offline.online && offline.waiting && !onTill ? (
          <div className="conn-bar" role="status">
            <span className="spinner" aria-hidden="true" />
            <span>{tn('offline.sending_bar', offline.waiting)}</span>
          </div>
        ) : visitors.length && !location.pathname.startsWith('/frontdesk') ? (
          <div className="conn-bar conn-bar-info fd-bar" role="status">
            <Icon name="contact" size={18} />
            <span>
              {visitors.length === 1
                ? t('fd.bar_one', { name: visitors[0]!.company ? `${visitors[0]!.name} (${visitors[0]!.company})` : visitors[0]!.name })
                : t('fd.bar_many', { n: visitors.length })}
            </span>
            {visitors.length === 1 ? (
              <Button size="sm" variant="primary" icon="send" onClick={() => void comingFor(visitors[0]!.id).then(() => toast(t('fd.told_reception')), fail)}>
                {t('fd.on_my_way')}
              </Button>
            ) : null}
            <NavLink to="/frontdesk?tab=mine" className="btn btn-ghost btn-sm">
              <span className="btn-label">{t('fd.bar_see')}</span>
            </NavLink>
          </div>
        ) : printProblems.length && !location.pathname.startsWith('/manage/printers') ? (
          <div className="conn-bar conn-bar-danger" role="alert">
            <Icon name="printer" size={18} />
            <span>
              {printProblems.length === 1 ? t('printing.problem_one', { name: printProblems[0]!.name, error: printProblems[0]!.error ?? '' }) : t('printing.problem_many', { n: printProblems.length })}
            </span>
            <Button size="sm" icon="undo" onClick={() => void retryPrints()}>
              {t('printing.try_again')}
            </Button>
            {can('printing.manage') ? (
              <NavLink to="/manage/printers" className="btn btn-ghost btn-sm">
                <span className="btn-label">{t('printing.open_printers')}</span>
              </NavLink>
            ) : null}
          </div>
        ) : clockDue && location.pathname !== '/team' ? (
          <div className="conn-bar conn-bar-info" role="status">
            <Icon name="clock" size={18} />
            <span>{clockDue.started ? t('team.bar_started', { time: clockDue.start }) : t('team.bar_soon', { time: clockDue.start })}</span>
            <NavLink to="/team" className="btn btn-primary btn-sm">
              <span className="btn-label">{t('team.clock_in')}</span>
            </NavLink>
          </div>
        ) : null}
        <div className={`shell-body${hasNav ? ' has-nav' : ''}`}>{children}</div>
      </div>
    </div>
  );
}

/** The grouped navigation. Expanded on a computer, a narrow icon rail when folded, a drawer on a phone. */
function Sidebar({
  entries,
  collapsed,
  onCollapse,
  open,
  onClose,
  tenantName,
}: {
  entries: NavEntry[];
  collapsed: boolean;
  onCollapse: () => void;
  open: boolean;
  onClose: () => void;
  tenantName: string;
}) {
  const location = useLocation();
  const [favs, setFavs] = usePref<string[]>('nav.fav', []);
  // Only the group of the open page starts open, so the menu stays short; people open more as they like.
  const [opened, setOpened] = usePref<Partial<Record<NavGroup, boolean>>>('nav.groups', {});
  const active = entryFor(entries, location.pathname);
  // A short menu (a waiter, a cook) needs no groups: just the places.
  const flat = entries.length <= 8;
  const favEntries = favs.map((to) => entries.find((e) => e.to === to)).filter((e): e is NavEntry => !!e);
  const toggleFav = (to: string) => setFavs(favs.includes(to) ? favs.filter((x) => x !== to) : [...favs, to]);
  const toggleGroup = (g: NavGroup, isOpen: boolean) => setOpened({ ...opened, [g]: !isOpen });
  const link = (x: NavEntry, inFav = false) => (
    <li key={(inFav ? 'f' : '') + x.to} className="side-item">
      <NavLink to={x.to} end={x.to === '/manage'} className={({ isActive }) => `side-link${isActive || active?.to === x.to ? ' active' : ''}`} data-tip={x.label}>
        <Icon name={x.icon} size={18} />
        <span className="side-label">{x.label}</span>
        {x.badge ? (
          <span className="nav-badge" aria-label={x.badgeLabel}>
            {x.badge > 99 ? '99+' : x.badge}
          </span>
        ) : null}
      </NavLink>
      <button
        type="button"
        className={`side-star${favs.includes(x.to) ? ' on' : ''}`}
        aria-pressed={favs.includes(x.to)}
        aria-label={favs.includes(x.to) ? t('shell.unfavourite', { name: x.label }) : t('shell.favourite', { name: x.label })}
        title={favs.includes(x.to) ? t('shell.unfavourite', { name: x.label }) : t('shell.favourite', { name: x.label })}
        onClick={() => toggleFav(x.to)}
      >
        <Icon name="star" size={14} />
      </button>
    </li>
  );
  return (
    <>
      {open ? <div className="side-scrim" onClick={onClose} aria-hidden="true" /> : null}
      <nav className={`side${open ? ' open' : ''}`} aria-label={t('nav.main')}>
        <div className="side-brand">
          <span className="app-mark cube" aria-hidden="true">
            <BrandMark size={26} />
          </span>
          <span className="side-brand-text">
            <span className="side-brand-name">{t('app.name')}</span>
            <span className="side-brand-org">{tenantName}</span>
          </span>
          <button type="button" className="side-fold" onClick={onCollapse} aria-label={collapsed ? t('shell.expand') : t('shell.collapse')} title={collapsed ? t('shell.expand') : t('shell.collapse')}>
            <Icon name={collapsed ? 'chevronRight' : 'back'} size={16} />
          </button>
          <button type="button" className="side-close" onClick={onClose} aria-label={t('common.close')}>
            <Icon name="x" size={20} />
          </button>
        </div>
        <div className="side-scroll">
          {!flat ? (
            <ul className="side-list side-apps">
              <li className="side-item">
                <NavLink to="/home" className={({ isActive }) => `side-link${isActive ? ' active' : ''}`} data-tip={t('home.nav')}>
                  <Icon name="chart" size={18} />
                  <span className="side-label">{t('home.nav')}</span>
                </NavLink>
              </li>
              <li className="side-item">
                <NavLink to="/apps" className={({ isActive }) => `side-link${isActive ? ' active' : ''}`} data-tip={t('shell.apps')}>
                  <Icon name="apps" size={18} />
                  <span className="side-label">{t('shell.apps')}</span>
                </NavLink>
              </li>
            </ul>
          ) : null}
          {favEntries.length && !flat ? (
            <div className="side-group">
              <p className="side-heading">
                <Icon name="star" size={14} />
                <span>{t('shell.favourites')}</span>
              </p>
              <ul className="side-list">{favEntries.map((x) => link(x, true))}</ul>
            </div>
          ) : null}
          {flat ? (
            <ul className="side-list">{entries.map((x) => link(x))}</ul>
          ) : (
            GROUPS.map((g) => {
              const items = entries.filter((e) => e.group === g.key);
              if (!items.length) return null;
              const isOpen = opened[g.key] ?? active?.group === g.key;
              const waiting = items.reduce((n, e) => n + (e.badge ?? 0), 0);
              return (
                <div key={g.key} className={`side-group${isOpen ? '' : ' shut'}`}>
                  <button type="button" className="side-heading side-toggle" aria-expanded={isOpen} onClick={() => toggleGroup(g.key, isOpen)}>
                    <span>{t(g.label)}</span>
                    {!isOpen && waiting ? <span className="side-dot" aria-label={t('nav.more_waiting', { n: waiting })} /> : null}
                    <Icon name="chevronRight" size={14} />
                  </button>
                  <ul className="side-list">{items.map((x) => link(x))}</ul>
                </div>
              );
            })
          )}
        </div>
      </nav>
    </>
  );
}

/** What waits for this person across the app, from the same live counts as the menu badges. */
function Notifications({ entries }: { entries: NavEntry[] }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, box, () => setOpen(false));
  const items = entries.filter((e) => e.badge);
  const n = items.reduce((a, e) => a + (e.badge ?? 0), 0);
  return (
    <div className="pop" ref={box}>
      <button
        type="button"
        className="icon-btn btn-ghost bell"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={n ? tn('shell.notifications_n', n) : t('shell.notifications')}
        title={t('shell.notifications')}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="bell" />
        {n ? <span className="bell-dot" aria-hidden="true" /> : null}
      </button>
      {open ? (
        <div className="pop-menu pop-end notif" role="menu" aria-label={t('shell.notifications')}>
          <p className="notif-title">{t('shell.notifications')}</p>
          {items.length ? (
            items.map((e) => (
              <button
                key={e.to}
                type="button"
                role="menuitem"
                className="pop-item notif-item"
                onClick={() => {
                  setOpen(false);
                  navigate(e.to);
                }}
              >
                <span className="notif-icon">
                  <Icon name={e.icon} size={18} />
                </span>
                <span className="notif-text">
                  <strong>{e.label}</strong>
                  <span>{e.badgeLabel}</span>
                </span>
                <span className="nav-badge static">{(e.badge ?? 0) > 99 ? '99+' : e.badge}</span>
              </button>
            ))
          ) : (
            <p className="notif-empty">
              <Icon name="checkCircle" size={18} />
              <span>{t('shell.notifications_none')}</span>
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Help: keyboard shortcuts and the design system. */
/** Quick create: a new invoice, customer, order… from anywhere, as far as the person may. */
function CreateMenu() {
  const navigate = useNavigate();
  const { can } = useSession();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, box, () => setOpen(false));
  const items = quickCreates(can);
  if (!items.length) return null;
  return (
    <div className="pop topbar-create" ref={box}>
      <button type="button" className="btn btn-primary btn-md topbar-create-btn" aria-haspopup="menu" aria-expanded={open} title={t('qc.menu')} onClick={() => setOpen((o) => !o)}>
        <Icon name="plus" size={18} />
        <span className="topbar-create-text">{t('qc.title')}</span>
      </button>
      {open ? (
        <div className="pop-menu pop-end" role="menu" aria-label={t('qc.menu')}>
          {items.map((c) => (
            <button
              key={c.key}
              type="button"
              role="menuitem"
              className="pop-item"
              onClick={() => {
                setOpen(false);
                navigate(c.to);
              }}
            >
              <Icon name={c.icon} size={18} />
              <span>{c.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function HelpMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [keys, setKeys] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, box, () => setOpen(false));
  return (
    <div className="pop topbar-help" ref={box}>
      <button type="button" className="icon-btn btn-ghost" aria-haspopup="menu" aria-expanded={open} aria-label={t('shell.help')} title={t('shell.help')} onClick={() => setOpen((o) => !o)}>
        <Icon name="help" />
      </button>
      {open ? (
        <div className="pop-menu pop-end" role="menu" aria-label={t('shell.help')}>
          <button
            type="button"
            role="menuitem"
            className="pop-item"
            onClick={() => {
              setOpen(false);
              setKeys(true);
            }}
          >
            <Icon name="hash" size={18} />
            <span>{t('shell.shortcuts')}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="pop-item"
            onClick={() => {
              setOpen(false);
              navigate('/design');
            }}
          >
            <Icon name="layout" size={18} />
            <span>{t('dsg.title')}</span>
          </button>
        </div>
      ) : null}
      {keys ? (
        <Dialog title={t('shell.shortcuts')} size="sm" onClose={() => setKeys(false)}>
          <dl className="keys">
            <dt>
              <kbd>{t('shell.search_keys')}</kbd>
            </dt>
            <dd>{t('shell.key_search')}</dd>
            <dt>
              <kbd>Esc</kbd>
            </dt>
            <dd>{t('shell.key_close')}</dd>
            <dt>
              <kbd>↑</kbd> <kbd>↓</kbd> <kbd>↵</kbd>
            </dt>
            <dd>{t('shell.key_move')}</dd>
          </dl>
        </Dialog>
      ) : null}
    </div>
  );
}

/** The person: name, business, theme, language and sign out in one place. */
function ProfileMenu({ name, business }: { name: string; business: string }) {
  const { signOut } = useSession();
  const navigate = useNavigate();
  const { theme, setTheme } = useUi();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, box, () => setOpen(false));
  const initials = (name || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const langs = availableLanguages();
  const lang = currentLang();
  return (
    <div className="pop" ref={box}>
      <button type="button" className="user-chip" aria-haspopup="menu" aria-expanded={open} aria-label={t('shell.profile', { name })} onClick={() => setOpen((o) => !o)}>
        <span className="avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="user-name">{name}</span>
        <Icon name="down" size={14} />
      </button>
      {open ? (
        <div className="pop-menu pop-end profile" role="menu" aria-label={t('shell.profile', { name })}>
          <div className="profile-head">
            <span className="avatar lg" aria-hidden="true">
              {initials}
            </span>
            <span>
              <strong>{name}</strong>
              <span className="muted small">{business}</span>
            </span>
          </div>
          <p className="pop-label">{t('shell.theme')}</p>
          <div className="theme-pick" role="radiogroup" aria-label={t('shell.theme')}>
            {(['light', 'dark', 'system'] as const).map((k) => (
              <button key={k} type="button" role="radio" aria-checked={theme === k} className={`theme-opt${theme === k ? ' on' : ''}`} onClick={() => setTheme(k)}>
                <Icon name={k === 'light' ? 'sun' : k === 'dark' ? 'moon' : 'circle'} size={16} />
                <span>{t(`shell.theme_${k}`)}</span>
              </button>
            ))}
          </div>
          {langs.length > 1 ? (
            <>
              <p className="pop-label">{t('lang.choose')}</p>
              {langs.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  role="menuitemradio"
                  aria-checked={l.code === lang}
                  lang={l.code}
                  className="pop-item"
                  onClick={() => {
                    setOpen(false);
                    setLang(l.code);
                  }}
                >
                  <Icon name={l.code === lang ? 'checkCircle' : 'globe'} size={18} />
                  <span>{l.name}</span>
                </button>
              ))}
            </>
          ) : null}
          <BusinessSwitch onDone={() => setOpen(false)} />
          <hr className="pop-divider" />
          <button
            type="button"
            role="menuitem"
            className="pop-item"
            onClick={() => {
              setOpen(false);
              navigate('/security');
            }}
          >
            <Icon name="shieldCheck" size={18} />
            <span>{t('sec.title')}</span>
          </button>
          <button type="button" role="menuitem" className="pop-item" onClick={() => void signOut()}>
            <Icon name="logout" size={18} />
            <span>{t('nav.sign_out')}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The businesses kept on this device (a group, or an accountant with several clients): switch to one, or add
 * another. Each one stays a separate sign-in; offline sales not yet sent keep the person in their business.
 */
function BusinessSwitch({ onDone }: { onDone: () => void }) {
  const [others] = useState(otherAccounts);
  const [blocked, setBlocked] = useState(false);
  const leave = (then: () => void) => {
    if (unsentSales()) return setBlocked(true);
    onDone();
    then();
  };
  return (
    <>
      <hr className="pop-divider" />
      <p className="pop-label">{t('biz.switch')}</p>
      {others.map((a) => (
        <button
          key={a.key}
          type="button"
          role="menuitem"
          className="pop-item biz-item"
          onClick={() =>
            leave(() => {
              stashCurrent();
              if (restore(a.key)) go();
            })
          }
        >
          <Icon name="store" size={18} />
          <span>
            {a.business}
            <span className="muted small"> · {a.user}</span>
          </span>
        </button>
      ))}
      <button
        type="button"
        role="menuitem"
        className="pop-item"
        onClick={() =>
          leave(() => {
            stashCurrent();
            go('/');
          })
        }
      >
        <Icon name="plus" size={18} />
        <span>{t('biz.add')}</span>
      </button>
      {blocked ? (
        <p className="pop-note" role="alert">
          {t('biz.unsent')}
        </p>
      ) : null}
    </>
  );
}

/** Closes a popover on Escape or a click outside it. */
function useDismiss(open: boolean, box: React.RefObject<HTMLElement | null>, close: () => void) {
  const fn = useRef(close);
  fn.current = close;
  useEffect(() => {
    if (!open) return;
    const on = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node)) fn.current();
    };
    document.addEventListener('pointerdown', on);
    document.addEventListener('keydown', on);
    return () => {
      document.removeEventListener('pointerdown', on);
      document.removeEventListener('keydown', on);
    };
  }, [open, box]);
}

/**
 * Where this device works, when the business has more than one location. A till follows its
 * register; a waiter's phone or a kitchen screen picks here once and keeps it.
 */
function PlaceSwitch() {
  const { places, here, multi, setHere } = usePlaces();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  if (!multi || !here) return null;
  return (
    <div className="place-switch" ref={box}>
      <button type="button" className="place-chip" aria-haspopup="true" aria-expanded={open} title={t('places.this_device', { name: here.name })} onClick={() => setOpen((o) => !o)}>
        <Icon name="pin" size={16} />
        <span className="place-chip-name">{here.name}</span>
        <span className="place-chip-code">{here.code}</span>
      </button>
      {open ? (
        <div className="topnav-sheet place-sheet" role="menu" aria-label={t('places.choose')}>
          <p className="place-sheet-title">{t('places.choose')}</p>
          {places.map((p) => (
            <button
              key={p.id}
              type="button"
              role="menuitemradio"
              aria-checked={p.id === here.id}
              className={`topnav-sheet-link${p.id === here.id ? ' active' : ''}`}
              onClick={() => {
                setHere(p.id);
                setOpen(false);
              }}
            >
              <Icon name={p.id === here.id ? 'checkCircle' : 'store'} size={20} />
              <span className="place-sheet-name">
                <span>{p.name}</span>
                {p.address ? <span className="muted small">{p.address}</span> : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
